import { i18nFn, I18nFormatArgs, I18nReacher, PointBalanceDTO } from '@springbig/shared';
import { Caption, H3 } from '~/shared/Typography';
import { Box, VStack, HStack, Actionsheet, Button } from 'native-base';
import { CardTitle } from '~/pages/homePage/components/CardTitle';
import { useHomePageStyles } from '~/shared/styles';
import React, { useState } from 'react';
import {
    faCalendarCheck,
    faClock,
    faLocationDot,
    faReceipt
} from '@fortawesome/free-solid-svg-icons';
import { SpIcon } from '~/shared/Icon';
import { format, isAfter } from 'date-fns';
import { useAppNavigate } from '~/pages';
import { CardBottomLink } from '~/pages/homePage/components/CardBottomLink';

type PointBalanceProps = {
    data: PointBalanceDTO;
    t: i18nFn;
};

export const PointBalance = ({
    data,
    t,
}: PointBalanceProps) => {

    const { navigate, params } = useAppNavigate();
    const [isOpenBonusRules, setIsOpenBonusRules] = useState<boolean>(false);
    const {
        button_background,
        button_text,
        primary_color,
        pressable_text,
        icon,
        sub_text,
    } = useHomePageStyles();

    const onClosePlanel = (): void => {

        setIsOpenBonusRules(false);
    };

    const categories = {
        'day_of_the_week': faCalendarCheck,
        'order_source': faLocationDot,
        'transaction_location': faLocationDot,
        'time_of_day_range': faClock,
        'points_earned_range': faReceipt,
        'transaction_total_range': faReceipt,
    };

    const handleCondition = (type: string): string | undefined => {

        switch (type) {
            case 'single': return undefined;
            case 'and': return t('home_page.point_balance.and');
            case 'or': return t('home_page.point_balance.or');
        }
    };

    const handleValues = (type: string, values: Array<string>): string => {

        const types = {
            'day_of_the_week': [
                t((`home_page.point_balance.week_days[${values[0]}]`) as I18nReacher)
            ],
            'order_source': [
                t((`home_page.point_balance.locations[${values[0]}]`) as I18nReacher)
            ],
            'transaction_location': values,
            'time_of_day_range': values,
            'points_earned_range': values,
            'transaction_total_range': values,
        };
        return types[type];
    };

    return (
        <>
            <VStack space={4}>
                <CardTitle
                    title={t('home_page.point_balance.title')}
                />
                <VStack space={4}>
                    <HStack space={2} justifyContent={'space-around'}>
                        <VStack
                            alignSelf={'center'}
                            alignItems={'center'}
                            bg={primary_color}
                            rounded={'lg'}
                            p={2}
                            space={2}
                            minW={24}
                        >
                            <Caption fontWeight={500}>
                                { t('home_page.point_balance.total_points') }
                            </Caption>
                            <Caption color={pressable_text}>
                                { data.balance }
                            </Caption>
                        </VStack>
                        { data.bonus_offer &&
                            <VStack
                                justifyContent={'space-between'}
                                alignItems={'center'}
                                bg={primary_color}
                                rounded={'lg'}
                                p={2}
                                space={2}
                                flex={1}
                            >
                                <Caption fontWeight={500}>
                                    { t('home_page.point_balance.bonus_points.title') }
                                </Caption>
                                <VStack alignItems={'center'}>
                                    { data.bonus_offer.type === 'add' ?
                                        <Caption color={pressable_text}>
                                            { t('home_page.point_balance.bonus_points.earning_prefix_addition', [data.bonus_offer.value]) }
                                        </Caption>
                                        :
                                        <Caption color={pressable_text}>
                                            { t('home_page.point_balance.bonus_points.earning_prefix_multiplication', [data.bonus_offer.value]) }
                                        </Caption>
                                    }
                                </VStack>
                                { data.bonus_offer?.active ?
                                    <Caption color={pressable_text}>
                                        { t('home_page.point_balance.bonus_points.earning') }
                                    </Caption>
                                    :
                                    <Button
                                        bg={button_background}
                                        py={1}
                                        px={4}
                                        rounded={'full'}
                                        onPress={() => navigate(`w/${params.wallet_id}/offers`)}
                                    >
                                        { data.bonus_offer.max_redemptions === -1 ?
                                            <Caption color={button_text}>
                                                { t('home_page.point_balance.bonus_points.unlimited') }
                                            </Caption>
                                            :
                                            <Caption color={button_text}>
                                                { t('home_page.point_balance.bonus_points.get', [data.bonus_offer.max_redemptions]) }
                                            </Caption>
                                        }
                                    </Button>
                                }
                                {
                                    data.bonus_offer.expiration_date &&
                                    isAfter(new Date(data.bonus_offer.expiration_date), new Date()) &&
                                    <Caption fontSize={10} textAlign={'center'} color={sub_text}>
                                        {
                                            t(
                                                'home_page.offers.expiration',
                                                [format(new Date(data.bonus_offer.expiration_date), 'MM/dd/yy')]
                                            )
                                        }
                                    </Caption>
                                }
                            </VStack>
                        }
                        { data.last_points_earned &&
                            <VStack
                                alignSelf={'center'}
                                alignItems={'center'}
                                bg={primary_color}
                                rounded={'lg'}
                                p={2}
                                space={2}
                                minW={24}
                            >
                                <Caption fontWeight={500}>
                                    { t('home_page.point_balance.last_earned') }
                                </Caption>
                                <Caption color={pressable_text}>
                                    { data.last_points_earned }
                                </Caption>
                            </VStack>
                        }
                    </HStack>
                </VStack>
                { data.bonus_rules && data.bonus_rules.length > 0 &&
                    <CardBottomLink
                        button={'primary'}
                        t={t}
                        text={'home_page.point_balance.learn_more'}
                        onClick={() => setIsOpenBonusRules(true)}
                    />
                }
            </VStack>
            { isOpenBonusRules &&
                <Actionsheet isOpen={isOpenBonusRules} onClose={onClosePlanel}>
                    <Actionsheet.Content
                        zIndex={9999}
                        overflow='auto'
                        w={'clamp(320px, 100vw, 480px)'}
                        alignSelf='center'
                        px={8}
                    >
                        <H3
                            alignSelf={'center'}
                            color={pressable_text}
                            fontSize={20}
                            fontWeight={400}
                        >
                            { t('home_page.point_balance.bonus_points.title') }
                        </H3>
                        <VStack
                            w={'100%'}
                            my={4}
                            borderColor={button_background}
                            borderWidth={2}
                            rounded={'xl'}
                        >
                            { data.bonus_rules.map((rule, index) =>

                                <HStack
                                    key={index}
                                    space={2}
                                    borderColor={button_background}
                                    borderBottomWidth={index < data.bonus_rules.length - 1 ? 2 : 0}
                                    h={20}
                                    alignSelf={'center'}
                                    w={'100%'}
                                    rounded={'xl'}
                                >
                                    <HStack
                                        space={1}
                                        justifyContent={'center'}
                                        alignItems={'center'}
                                        p={4}
                                        w={20}
                                    >
                                        { rule.conditions?.map((condition, condition_index) =>

                                            <Box
                                                key={condition_index}
                                                mt={rule.conditions.length > 1 && condition_index === 0 ? -5 : 0}
                                                mb={rule.conditions.length > 1 && condition_index > 0 ? -5 : 0}
                                                position={'relative'}
                                            >
                                                { rule.conditions.length > 1 && condition_index > 0 &&
                                                    <Caption
                                                        position={'absolute'}
                                                        left={0}
                                                        top={-18}
                                                        fontSize={12}
                                                        opacity={.5}
                                                    >
                                                        { handleCondition(rule.condition_type) }
                                                    </Caption>
                                                }
                                                <SpIcon
                                                    icon={categories[condition.type]}
                                                    color={icon}
                                                    size={rule.conditions.length > 1 ? 18 : 36}
                                                />
                                            </Box>
                                        )}
                                    </HStack>
                                    <Caption
                                        alignSelf={'center'}
                                        textAlign={'center'}
                                        p={4}
                                        pl={0}
                                        flex={1}
                                        color={pressable_text}
                                    >
                                        <Caption fontSize={16}>
                                            { t('home_page.point_balance.bonus_rules.earning_prefix_multiplication', [rule.points]) + ' ' }
                                        </Caption>
                                        {
                                            rule.conditions.length > 0 &&
                                            rule.conditions.map((condition, condition_index) =>

                                                <Caption key={condition_index}>
                                                    <Caption fontSize={16}>
                                                        { t(
                                                            (`home_page.point_balance.bonus_rules[${condition.type}]`) as I18nReacher,
                                                            handleValues(condition.type, condition.values)
                                                        ) }
                                                    </Caption>
                                                    {
                                                        rule.human_readable_rule.length > 1 &&
                                                        condition_index < rule.conditions.length - 1 &&
                                                        <Caption fontSize={16} fontWeight={700}>
                                                            { ' ' + rule.condition_type + ' ' }
                                                        </Caption>
                                                    }
                                                </Caption>
                                            )}
                                    </Caption>
                                </HStack>
                            )}
                        </VStack>
                    </Actionsheet.Content>
                </Actionsheet>
            }
        </>
    );
};
